import type { $Enums } from '@prisma/client';
import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';

/**
 * THE BUY-SELL ORDER HEADER — status windows, the lifecycle table and the delete / revert / close
 * rules, asked by the BSO page AND by the api before it writes.
 *
 *   edit(field) ──▶ status window ──▶ lineItems: no open invoice
 *   delete ──────▶ default template? ──▶ DRAFT only
 *   perform(op) ─▶ op in the status's lifecycle table ──▶ revert: no paid bill (PAYMENT_RECONCILIATION)
 *                                                     └─▶ close: invoice + payable finalized
 */

export type BuySellOrderStatus = $Enums.BuySellOrderStatus;

type BillStatus = $Enums.InvoiceStatus;
type BillType = $Enums.BillType;

export type BuySellOrderFacts = {
  status: BuySellOrderStatus;
  /**
   * Any INVOICE bill on the order is open (`countOpenBuySellOrderBills`). Locks the products.
   * Undefined = not judged: the api cannot tell an order-page edit from the bill page that owns the invoice.
   */
  hasOpenInvoice?: boolean;
  /** The order is its organization's default template. Undefined = not known, treated as not. */
  isDefaultTemplate?: boolean;
  /** A non-standalone bill is paid (`BUY_SELL_ORDER_PAID_BILL_STATUSES`). Undefined = not known, treated as none. */
  hasPaidBill?: boolean;
  /** Closing must wait for the active invoice and payable (`buySellOrderBillsBlockClose`). Undefined = not known, treated as not. */
  billsBlockClose?: boolean;
};

// ── Shared fact predicates: the web and the api compute each fact the SAME way ───────────

/** Bill statuses that no longer hold the order: a bill in one of these is not open. */
export const BUY_SELL_ORDER_CLOSED_BILL_STATUSES: readonly BillStatus[] = ['DRAFT', 'VOIDED', 'CANCELLED'];

export const buySellOrderBillIsOpen = (bill: { status: string }): boolean =>
  !BUY_SELL_ORDER_CLOSED_BILL_STATUSES.some((status) => status === bill.status);

/** How many of the order's bills of one type are open — the Products grid's invoice and payable counts. */
export const countOpenBuySellOrderBills = (
  bills: readonly { type: string; status: string }[],
  type: BillType
): number => bills.filter((bill) => bill.type === type && buySellOrderBillIsOpen(bill)).length;

/** Bill statuses that block reverting an order out of PAYMENT_RECONCILIATION. */
export const BUY_SELL_ORDER_PAID_BILL_STATUSES: readonly BillStatus[] = ['PAID_PARTIAL', 'PAID_FULL', 'FINALIZED'];

/**
 * Close waits until the order's ACTIVE (neither VOIDED nor DRAFT) BSO payable and invoice are both
 * FINALIZED. Only one of each is active at a time. The caller decides whether the rule applies at
 * all (bills attached, accounting on).
 */
export const buySellOrderBillsBlockClose = (
  attachments: readonly { type: string; bill: { type: string; status: string } }[]
): boolean => {
  const active = (billType: BillType) =>
    attachments.find(
      ({ type, bill }) =>
        type === 'BSO' && bill.type === billType && bill.status !== 'VOIDED' && bill.status !== 'DRAFT'
    );

  return active('PAYABLE')?.bill.status !== 'FINALIZED' || active('INVOICE')?.bill.status !== 'FINALIZED';
};

// ── Fields ───────────────────────────────────────────────────────────────────────────────

export const BUY_SELL_ORDER_FIELDS = [
  'broker',
  'orderDate',
  'vendor',
  'vendorContact',
  'customerPO',
  'vendorSO',
  'purchaseTerms',
  'billTo',
  'origin',
  'customer',
  'customerContact',
  'salesTerms',
  'destination',
  'shippingTerms',
  'freightCompany',
  'shipDate',
  'deliveryDate',
  'lineItems',
  'expenses',
  'currency',
  'subsidiary',
] as const;

export type BuySellOrderField = (typeof BUY_SELL_ORDER_FIELDS)[number];

const TO_SUBMITTED: readonly BuySellOrderStatus[] = ['DRAFT', 'SUBMITTED'];
const TO_PAYMENT_RECONCILIATION: readonly BuySellOrderStatus[] = [...TO_SUBMITTED, 'PAYMENT_RECONCILIATION'];
const TO_CLOSED: readonly BuySellOrderStatus[] = [...TO_PAYMENT_RECONCILIATION, 'CLOSED'];

/** The statuses in which a buy-sell order's products can change — the line item policy's window. */
export const BUY_SELL_ORDER_LINE_ITEMS_EDITABLE_STATUSES: readonly BuySellOrderStatus[] = TO_CLOSED;

/** The statuses in which a buy-sell order's charges can change. */
export const BUY_SELL_ORDER_EXPENSES_EDITABLE_STATUSES: readonly BuySellOrderStatus[] = TO_CLOSED;

const statusAllowsField = (status: BuySellOrderStatus, field: BuySellOrderField): boolean => {
  switch (field) {
    case 'broker':
    case 'orderDate':
      return TO_SUBMITTED.includes(status);
    case 'vendor':
    case 'vendorContact':
    case 'customerPO':
    case 'vendorSO':
    case 'purchaseTerms':
    case 'billTo':
    case 'origin':
    case 'customer':
    case 'customerContact':
    case 'salesTerms':
    case 'destination':
    case 'shippingTerms':
    case 'freightCompany':
    case 'shipDate':
    case 'deliveryDate':
    case 'currency':
      return TO_PAYMENT_RECONCILIATION.includes(status);
    case 'lineItems':
      return BUY_SELL_ORDER_LINE_ITEMS_EDITABLE_STATUSES.includes(status);
    case 'expenses':
      return BUY_SELL_ORDER_EXPENSES_EDITABLE_STATUSES.includes(status);
    case 'subsidiary':
      return true;
  }
};

/** The products lock while an invoice is open; shared with the line item policy. */
export const buySellOrderInvoiceLock = (): Decision =>
  deny('BUY_SELL_ORDER_INVOICE_LOCKED', 'Products are locked while the invoice is open.');

const decideField = (facts: BuySellOrderFacts, field: BuySellOrderField): Decision => {
  if (!statusAllowsField(facts.status, field)) {
    return deny(
      'BUY_SELL_ORDER_FIELD_LOCKED',
      `This field is locked once the buy-sell order is ${getStatusLabel(facts.status)}.`
    );
  }

  if (field === 'lineItems' && facts.hasOpenInvoice === true) return buySellOrderInvoiceLock();

  return allow();
};

// ── Lifecycle ────────────────────────────────────────────────────────────────────────────

/** The events the Buy-Sell Order state machine accepts (api/src/services/buySellOrders/buySellOrder.states.ts). */
export const BUY_SELL_ORDER_LIFECYCLE_EVENTS = [
  'submit',
  'reconcile_payments',
  'close',
  'revert',
  'cancel',
  'uncancel',
] as const;

export type BuySellOrderLifecycleEvent = (typeof BUY_SELL_ORDER_LIFECYCLE_EVENTS)[number];

export const BUY_SELL_ORDER_OPS = ['submit', 'reconcile payments', 'close', 'revert', 'cancel', 'uncancel'] as const;

export type BuySellOrderOp = (typeof BUY_SELL_ORDER_OPS)[number];

/** ONE event → operation mapping, so the api door and the header button ask the SAME question. */
export const buySellOrderLifecycleOperation = (event: BuySellOrderLifecycleEvent): BuySellOrderOp => {
  switch (event) {
    case 'reconcile_payments':
      return 'reconcile payments';
    case 'submit':
    case 'close':
    case 'revert':
    case 'cancel':
    case 'uncancel':
      return event;
  }
};

/**
 * THE STATE MACHINE'S `on` TABLE. An op outside its status's row is the machine's "not a valid
 * event" refusal. Pinned cell by cell against the machine in
 * api/src/services/buySellOrders/buySellOrder.policy.characterization.test.ts.
 */
const LIFECYCLE: Record<BuySellOrderStatus, readonly BuySellOrderOp[]> = {
  DRAFT: ['submit', 'cancel'],
  SUBMITTED: ['reconcile payments', 'cancel', 'revert'],
  PAYMENT_RECONCILIATION: ['close', 'revert'],
  CLOSED: ['revert'],
  CANCELLED: ['uncancel'],
};

/** The labels shown by the order header's primary and confirmation buttons. */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  submit: 'Submit',
  'reconcile payments': 'Reconcile Payments',
  close: 'Close',
  revert: 'Revert Buy-Sell Order',
  cancel: 'Cancel Buy-Sell Order',
  uncancel: 'Uncancel Buy-Sell Order',
};

export const buySellOrderInvalidEvent = (status: BuySellOrderStatus, op: string) =>
  deny(
    'BUY_SELL_ORDER_INVALID_EVENT',
    `${ACTION_LABELS[op] ?? 'This action'} isn't available while the buy-sell order is ${getStatusLabel(status)}.`
  );

const decideOperation = (facts: BuySellOrderFacts, op: BuySellOrderOp): Decision => {
  if (!LIFECYCLE[facts.status].includes(op)) return buySellOrderInvalidEvent(facts.status, op);

  if (op === 'revert' && facts.status === 'PAYMENT_RECONCILIATION' && facts.hasPaidBill === true) {
    return deny('BUY_SELL_ORDER_REVERT_BILL_PAID', "Can't revert: a bill on this buy-sell order is paid.");
  }

  if (op === 'close' && facts.billsBlockClose === true) {
    return deny(
      'BUY_SELL_ORDER_CLOSE_BILLS_NOT_FINALIZED',
      'The invoice and payable must be finalized to close this buy-sell order.'
    );
  }

  return allow();
};

export const buySellOrderDefaultTemplateLock = () =>
  deny('BUY_SELL_ORDER_DEFAULT_TEMPLATE_LOCKED', "The default template can't be deleted.");

const decideDelete = (facts: BuySellOrderFacts): Decision => {
  if (facts.isDefaultTemplate === true) {
    return buySellOrderDefaultTemplateLock();
  }

  if (facts.status !== 'DRAFT') {
    return deny('BUY_SELL_ORDER_DELETE_LOCKED', 'Only Draft buy-sell orders can be deleted.');
  }

  return allow();
};

type BuySellOrderAction = Action<BuySellOrderField, BuySellOrderOp>;

const fieldRule = (facts: BuySellOrderFacts, action: BuySellOrderAction): Decision =>
  action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow();

const fieldRules = {
  broker: fieldRule,
  orderDate: fieldRule,
  vendor: fieldRule,
  vendorContact: fieldRule,
  customerPO: fieldRule,
  vendorSO: fieldRule,
  purchaseTerms: fieldRule,
  billTo: fieldRule,
  origin: fieldRule,
  customer: fieldRule,
  customerContact: fieldRule,
  salesTerms: fieldRule,
  destination: fieldRule,
  shippingTerms: fieldRule,
  freightCompany: fieldRule,
  shipDate: fieldRule,
  deliveryDate: fieldRule,
  lineItems: fieldRule,
  expenses: fieldRule,
  currency: fieldRule,
  subsidiary: fieldRule,
} satisfies Record<BuySellOrderField, typeof fieldRule>;

export const buySellOrderPolicy: Policy<BuySellOrderFacts, BuySellOrderField, BuySellOrderOp> = definePolicy<
  BuySellOrderFacts,
  BuySellOrderField,
  BuySellOrderOp
>({
  kind: 'buy-sell order',
  fields: BUY_SELL_ORDER_FIELDS,
  ops: BUY_SELL_ORDER_OPS,
  requires: {},
  entity: (facts, action) => {
    switch (action.type) {
      case 'view':
      case 'add':
      case 'edit':
        return allow();
      case 'delete':
        return decideDelete(facts);
      case 'perform':
        return decideOperation(facts, action.op);
    }
  },
  field: fieldRules,
});
